import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { directLinkStateSelector, loadDirectLink, setStatus, setupDirectLink } from '@src/stores/directLink';

import SpinnerBar from '@src/components/reusable/spinner/SpinnerBar';

import { Wrapper, LoadingWrapper, GlobalResetStyles } from './styles';
import Error from './Error';
import { useAppDispatch, useAppSelector, useTranslate, useWidgetParam } from '@src/core/hooks';
import { setupAuth, setupAuthOngoingSelector } from '@src/features/customers';
import { setGlobalParameter } from '@src/stores/globalParameters';
import Checkout from './Checkout';
import Payment from './Payment';
import FontsProvider from '@src/fonts/FontsProvider';
import FontFamily from './FontFamily';
import { currentSaleSelector } from '@src/stores/sale';
import { currentOrderSelector } from '@src/features/orders';
import { CHECKOUT_DIRECT_LINK_TYPES, DirectLinkType } from '@src/domains/direct-links';
import { callHostFunction } from '@src/common/hostCallbacks';

type DirectLinkProps = {
  planneLinkCode?: string;
  customerAccessToken?: string;
  onFinishCheckout?: string;
  useDefaultFont?: boolean;
  currency?: string;
};

const DirectLink = (props: DirectLinkProps) => {
  const { i18n } = useTranslate();
  const dispatch = useAppDispatch();

  const { directLink, status, error } = useSelector(directLinkStateSelector);
  const setupAuthOngoing = useAppSelector(setupAuthOngoingSelector);

  const planneLinkCodeParam = useWidgetParam('planne-link-code');
  const customerAccessTokenParam = useWidgetParam('customer-access-token');
  const onFinishCheckoutParam = useWidgetParam('on-finish-checkout');
  const useDefaultFontParam = useWidgetParam('use-default-font');
  const currencyParam = useWidgetParam('currency');

  const currency = props.currency ?? currencyParam;
  const planneLinkCode = props.planneLinkCode ?? planneLinkCodeParam;
  const customerAccessToken = props.customerAccessToken ?? customerAccessTokenParam;
  const onFinishCheckout = props.onFinishCheckout ?? onFinishCheckoutParam;
  const useDefaultFont = props.useDefaultFont ?? useDefaultFontParam;
  const currentSale = useAppSelector(currentSaleSelector);
  const currentOrder = useAppSelector(currentOrderSelector);

  const [initiated, setInitiated] = useState(false);

  const isLoading = !initiated || status === 'loading' || setupAuthOngoing;

  const isCheckoutLink = CHECKOUT_DIRECT_LINK_TYPES.includes(directLink?.type ?? '');

  const isExpired = directLink?.isExpired;

  const onSuccess = () => {
    if (status !== 'finished') {
      callHostFunction(onFinishCheckout, directLink?.type === DirectLinkType.Order ? currentOrder : currentSale);

      dispatch(setStatus('finished'));
    }
  };

  useEffect(() => {
    if (initiated) {
      if (customerAccessToken) {
        dispatch(setupAuth(false, customerAccessToken));
      }

      if (planneLinkCode) {
        dispatch(loadDirectLink(planneLinkCode));
      }
    }
  }, [planneLinkCode, initiated]);

  useEffect(() => {
    if (!initiated) {
      dispatch(setupDirectLink());

      if (currency) {
        dispatch(setGlobalParameter('currency', currency));
      }

      setInitiated(true);
    }
  }, []);

  return (
    <>
      <FontsProvider />
      <GlobalResetStyles />

      {useDefaultFont === 'true' ? <FontFamily /> : null}

      <Wrapper>
        {isLoading ? (
          <LoadingWrapper>
            <SpinnerBar />
          </LoadingWrapper>
        ) : error ? (
          <Error>{error}</Error>
        ) : isExpired ? (
          <Error>{i18n('direct_link_expired_message')}</Error>
        ) : isCheckoutLink ? (
          <Checkout onSuccess={onSuccess} />
        ) : directLink?.type === DirectLinkType.Payment ? (
          <Payment onSuccess={onSuccess} />
        ) : (
          <Error>{i18n('direct_link_invalid_message')}</Error>
        )}
      </Wrapper>
    </>
  );
};

export default DirectLink;
